import React, { useState } from 'react'
import invariant from 'tiny-invariant'
import { getTimezoneCities, getTimezoneData } from '@chilipiper/date-time'
import { typeValidator } from '@chilipiper/utils'
import { Box, Flex, Text } from '../../../core-components'
import { SettingLabel } from '../../wayfinding/setting-label/SettingLabel'
import { OmitInternalProperties, PositioningProps } from '../../types'
import { BaseSelect, BaseSelectProps } from '../base-select/BaseSelect'
import { CurrentTimeDisplay } from '../timezone-list/CurrentTimeDisplay'
import { useTimezoneListData } from '../timezone-list/useTimezoneListData'
import { getFormatedLabelFromOffset } from './helpers'

type Props = Omit<
  BaseSelectProps,
  | 'listStateData'
  | 'hasLabelInside'
  | 'onChange'
  | 'startIcon'
  | 'setSelectedCascadeItem'
  | 'hasUserProvidedSearch'
  | 'isLoading'
  | 'search'
  | 'onSearchChange'
  | 'selectedCascadeItem'
  | 'selectedText'
  | 'valuePath'
  | 'hasSearch'
  | 'size'
> & {
  hasTime?: boolean
  locale?: string
  onChange?: (value: string) => void
  size?: 'S' | 'M'
  textSize?: 'regular' | 'large'
} & PositioningProps

export const TimezoneSelectInternal = ({
  hasTime,
  isDisabled,
  label,
  locale,
  value,
  onChange,
  size = 'M',
  textSize = 'regular',
  ...otherProps
}: Props) => {
  invariant(
    typeValidator(TimezoneSelectLabel)(label),
    'TimezoneSelectLabel: label must be a TimezoneSelectLabel.Label component'
  )

  const [searchValue, setSearchValue] = useState('')

  const { isLoading, listData } = useTimezoneListData({
    hasTime,
    locale,
    search: searchValue,
    value,
    size,
  })

  const props = {
    ...otherProps,
    isDisabled,
    value,
  }

  return (
    <BaseSelect
      {...props}
      hasSearch
      hasUserProvidedSearch={false}
      isLoading={isLoading}
      label={label}
      listStateData={listData}
      onChange={value => onChange?.(value)}
      onSearchChange={setSearchValue}
      search={searchValue}
      size={textSize}
      selectedText={selectedItem => {
        const timezoneCities = getTimezoneCities(selectedItem.key as string)
        const timezoneData = getTimezoneData(selectedItem.key as string)

        if (!timezoneData || !timezoneCities) {
          return (
            <Text color='fg/major' textStyle={textSize === 'large' ? 'form-value-l' : 'form-value'}>
              {selectedItem.rendered}
            </Text>
          )
        }

        const formattedCity = timezoneCities[0] ? `: ${timezoneCities[0]}` : ''

        return (
          <Flex alignItems='center' w='100%'>
            <Text
              color={isDisabled ? 'fg/disabled' : 'fg/moderate'}
              textStyle={textSize === 'large' ? 'form-value-l' : 'form-value'}
            >
              {getFormatedLabelFromOffset(timezoneData.offset)}
            </Text>
            <Text
              color={isDisabled ? 'fg/disabled' : 'fg/major'}
              ml={2}
              lineHeight={
                8 /* Overflow hidden clips the text otherwise, use negative margin to offset it */
              }
              my={-1}
              overflow='hidden'
              textOverflow='ellipsis'
              textStyle={textSize === 'large' ? 'form-value-l' : 'form-value'}
              whiteSpace='nowrap'
            >
              {`${timezoneData.abbreviation}${formattedCity}`}
            </Text>
            {hasTime && (
              <Box flexShrink={0} ml='auto' mr={-2}>
                <CurrentTimeDisplay locale={locale} timezone={selectedItem.key as string} />
              </Box>
            )}
          </Flex>
        )
      }}
    />
  )
}

const TimezoneSelectLabel = (props: React.ComponentProps<typeof SettingLabel>) => (
  <SettingLabel {...props} />
)

type PublicProps = OmitInternalProperties<Props>

export const TimezoneSelectPublic = (props: PublicProps) => <TimezoneSelectInternal {...props} />

TimezoneSelectInternal.Label = TimezoneSelectLabel
TimezoneSelectPublic.Label = TimezoneSelectLabel
